import React from "react";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import Pagination from "react-js-pagination";
import { clearLogs, getLogs } from "../../api/logs";
import ModalBox from "../../components/shared/pageComponent/ModalBox";
import { toast } from "react-toastify";
import { formatDateTime } from "../../utils/formatDate";

/** One height for every control in the toolbar, so they share a baseline. */
const CONTROL_H = 38;

/** Selects and the search box: fixed width, never stretched by the flex row. */
const controlStyle = { height: CONTROL_H, flex: "0 0 auto" };

/**
 * Buttons: `width: auto` is the load-bearing part. Bootstrap renders `.btn` as a
 * block, so in a wrapping flex row each one claimed the full line — Refresh and
 * Clear were rendering as full-width bars under the filters.
 */
const buttonStyle = {
  height: CONTROL_H,
  width: "auto",
  flex: "0 0 auto",
  padding: "0 16px",
  whiteSpace: "nowrap",
};

/** Colour a level so fatal/error/warn/info read at a glance. */
const LEVEL_CLASS = {
  fatal: "badge badge-danger",
  error: "badge badge-danger",
  warn: "badge badge-warning",
  info: "badge badge-secondary",
};

/**
 * App log viewer — crash/error/diagnostic rows the mobile app mirrors to the API
 * (`client_logs`). Lets staff SEE what failed on a device (and the live-audio
 * `[audio-diag]` trail) without a device console. Read-only, newest first.
 */
function Logs(props) {
  const [dataTable, setDataTable] = React.useState({
    data: [],
    pagination: { totalRecords: 0, totalPages: 0, currentPage: 1, pageSize: 20 },
  });
  const [level, setLevel] = React.useState("");
  // The API has always supported this filter; the UI never exposed it, so
  // server-side diagnostics (push drops, IAP verification outcomes — written
  // with platform "server") sat mixed in with every device's logs and could not
  // be read as a stream of their own.
  const [platform, setPlatform] = React.useState("");
  const [search, setSearch] = React.useState("");
  const [expanded, setExpanded] = React.useState({});
  // A failed load must NOT fall through to "No logs" — that would tell the
  // operator there is nothing when the request actually errored.
  const [loadError, setLoadError] = React.useState(false);
  // Clearing is destructive, so it is confirmed — and the dialog states exactly
  // WHAT will go, because the delete honours the filters currently applied.
  const [confirmingClear, setConfirmingClear] = React.useState(false);
  const [clearing, setClearing] = React.useState(false);

  React.useEffect(() => {
    if (props.setTitlePage) props.setTitlePage("App Logs");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadLogs = async (page = 1) => {
    setLoadError(false);
    try {
      const result = await props.getLogs({ page, level, platform, search });
      setDataTable(result);
    } catch (error) {
      console.error("Failed to load logs:", error);
      setLoadError(true);
    }
  };

  React.useEffect(() => {
    loadLogs(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [level, platform]);

  /** Human description of what the Clear button will actually remove. */
  const scopeLabel = () => {
    const parts = [];
    if (level) parts.push(`level "${level}"`);
    if (platform) parts.push(`source "${platform}"`);
    if (search) parts.push(`matching "${search}"`);
    return parts.length ? parts.join(", ") : "ALL log entries";
  };

  const onConfirmClear = async () => {
    setClearing(true);
    try {
      // NO trailing `()`. `connect`'s object form wraps this in `dispatch`, and
      // redux-thunk RUNS the thunk and hands back its promise — exactly as
      // `loadLogs` above awaits `props.getLogs(...)` with no second call. The
      // extra pair of parentheses called `()` on that promise, which throws
      // TypeError *after* axios has already fired the DELETE. So the logs were
      // deleted every time and the UI said "Could not clear the logs. Please try
      // again." — inviting a retry that deleted again. Destroying data and
      // reporting failure is the worst version of this bug class.
      const result = await props.clearLogs({ level, platform, search });
      toast(`Deleted ${result.deleted} log ${result.deleted === 1 ? "entry" : "entries"}`, {
        type: "success",
      });
      setConfirmingClear(false);
      loadLogs(1);
    } catch (error) {
      console.error("Failed to clear logs:", error);
      toast("Could not clear the logs. Please try again.", { type: "error" });
    } finally {
      setClearing(false);
    }
  };

  const onSearchSubmit = (e) => {
    e.preventDefault();
    loadLogs(1);
  };

  return (
    <>
      {/* One toolbar row, every control the SAME height and sized explicitly.
          Bootstrap's `.form-control` is `width: 100%` and `.btn` is a block, so
          inside a flex row they stretch and each wrapped item claimed a full
          line of its own — which is why Refresh and Clear rendered as full-width
          bars. Inline widths and `flex: 0 0 auto` beat the class rules, and a
          shared CONTROL_H keeps the selects, the input and the buttons on one
          baseline instead of three slightly different heights. */}
      <div
        className="d-flex flex-wrap align-items-center mb-3"
        style={{ gap: 8, rowGap: 8 }}
      >
        <select
          className="form-control"
          style={{ ...controlStyle, width: 150 }}
          value={level}
          onChange={(e) => setLevel(e.target.value)}
        >
          <option value="">All levels</option>
          <option value="fatal">Fatal</option>
          <option value="error">Error</option>
          <option value="warn">Warn</option>
          <option value="info">Info</option>
        </select>
        <select
          className="form-control"
          style={{ ...controlStyle, width: 150 }}
          value={platform}
          onChange={(e) => setPlatform(e.target.value)}
        >
          <option value="">All sources</option>
          <option value="server">Server</option>
          <option value="ios">iOS</option>
          <option value="android">Android</option>
        </select>
        <form
          onSubmit={onSearchSubmit}
          className="d-flex align-items-center"
          style={{ gap: 8, flex: "0 0 auto", margin: 0 }}
        >
          <input
            className="form-control"
            style={{ ...controlStyle, width: 240 }}
            placeholder="Search message…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <button type="submit" className="btn btn-primary" style={buttonStyle}>
            Search
          </button>
        </form>
        <button type="button" className="btn btn-outline-secondary" style={buttonStyle} onClick={() => loadLogs(1)}>
          Refresh
        </button>
        {/* Pushed to the far end and given the danger style: it is the only
            control here that destroys something, so it should not read as
            another filter. */}
        <button
          type="button"
          className="btn btn-outline-danger"
          style={{ ...buttonStyle, marginLeft: "auto" }}
          onClick={() => setConfirmingClear(true)}
        >
          Clear logs
        </button>
      </div>

      <ModalBox
        isOpen={confirmingClear}
        toggle={() => setConfirmingClear(false)}
        withHeader={true}
        ModalTitle="Clear logs?"
      >
        {/* An explicit column. The modal body inherits a flex row from the
            theme, which laid these three blocks out SIDE BY SIDE — the warning
            squeezed into a narrow column with the buttons floating beside it.
            Declaring the direction here makes the dialog independent of whatever
            the surrounding stylesheet does. */}
        <div style={{ display: "flex", flexDirection: "column", gap: 12, width: "100%" }}>
          <p style={{ margin: 0 }}>
            This permanently deletes <strong>{scopeLabel()}</strong>.
          </p>
          <p className="text-muted" style={{ margin: 0, fontSize: 13 }}>
            Deletion follows the filters above, so it removes exactly what the table is
            showing. Clear a single source or level rather than everything if you only
            want a clean slate for one test — the server-side diagnostics and other
            testers&apos; trails are evidence too.
          </p>
          <div className="d-flex justify-content-end" style={{ gap: 8 }}>
            <button
              type="button"
              className="btn btn-outline-secondary"
              style={buttonStyle}
              onClick={() => setConfirmingClear(false)}
              disabled={clearing}
            >
              Cancel
            </button>
            <button
              type="button"
              className="btn btn-danger"
              style={buttonStyle}
              onClick={onConfirmClear}
              disabled={clearing}
            >
              {clearing ? "Clearing…" : "Delete"}
            </button>
          </div>
        </div>
      </ModalBox>

      <div className="table-responsive">
        <table className="table table-sm table-bordered">
          <thead>
            <tr>
              <th style={{ whiteSpace: "nowrap" }}>Time</th>
              <th>Level</th>
              <th>Platform</th>
              <th>Version</th>
              <th>User</th>
              <th>Message</th>
            </tr>
          </thead>
          <tbody>
            {dataTable.data.map((row) => (
              <React.Fragment key={`log-${row.id}`}>
                <tr
                  style={{ cursor: "pointer" }}
                  onClick={() => setExpanded((e) => ({ ...e, [row.id]: !e[row.id] }))}
                >
                  <td style={{ verticalAlign: "middle", whiteSpace: "nowrap" }}>
                    {formatDateTime(row.createdAt)}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>
                    <span className={LEVEL_CLASS[row.level] || "badge badge-secondary"}>
                      {row.level}
                    </span>
                  </td>
                  <td style={{ verticalAlign: "middle" }}>{row.platform || "—"}</td>
                  <td style={{ verticalAlign: "middle" }}>{row.appVersion || "—"}</td>
                  {/* Name + email, not a bare id. Staff read these while a
                      specific tester is on the phone, and a number meant
                      cross-referencing the user list by hand for every row. */}
                  <td style={{ verticalAlign: "middle" }}>
                    {row.userId == null ? (
                      "—"
                    ) : (
                      <span>
                        {row.userName || `User ${row.userId}`}
                        <br />
                        <small className="text-muted">
                          {row.userEmail || "no email"} · #{row.userId}
                        </small>
                      </span>
                    )}
                  </td>
                  <td style={{ verticalAlign: "middle" }}>{row.message}</td>
                </tr>
                {expanded[row.id] && (row.stack || row.context) && (
                  <tr>
                    <td colSpan={6} style={{ background: "#f8f9fa" }}>
                      {row.stack && (
                        <pre style={{ whiteSpace: "pre-wrap", marginBottom: row.context ? 8 : 0 }}>
                          {row.stack}
                        </pre>
                      )}
                      {row.context && (
                        <pre style={{ whiteSpace: "pre-wrap", margin: 0 }}>
                          {JSON.stringify(row.context, null, 2)}
                        </pre>
                      )}
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
            {dataTable.data.length === 0 && (
              <tr>
                <td colSpan={6} className="text-center p-4">
                  {loadError ? (
                    <>
                      <div>Couldn&apos;t load logs.</div>
                      <button
                        type="button"
                        className="btn btn-link p-0"
                        onClick={() => loadLogs(dataTable.pagination.currentPage)}
                      >
                        Try again
                      </button>
                    </>
                  ) : (
                    "No logs found."
                  )}
                </td>
              </tr>
            )}
          </tbody>
        </table>
        <Pagination
          activePage={dataTable.pagination.currentPage}
          itemsCountPerPage={dataTable.pagination.pageSize}
          totalItemsCount={dataTable.pagination.totalRecords}
          pageRangeDisplayed={5}
          onChange={loadLogs}
        />
      </div>
    </>
  );
}

export default connect(null, { getLogs, clearLogs })(withRouter(Logs));
